<script lang="ts">
import { PropType, VNode, defineComponent, h, resolveComponent } from 'vue';

import { formatCurrency, formatDate } from '@/utils/filters';
import { IOpenInvoiceListItem } from '@/bundles/Invoice/types';
import { getAccountRouteHandler } from '@/bundles/App/helpers';

import Label from '@/bundles/Common/components/Label.vue';

export default defineComponent({
  name: 'OpenInvoiceMobileCard',

  inheritAttrs: false,

  props: {
    item: {
      type: Object as PropType<IOpenInvoiceListItem>,
      required: true
    },
    activeAccountId: {
      type: Number,
      required: true
    },
    selected: Boolean,
  },

  render (): VNode | VNode[] {
    const props = this.$props as { item: IOpenInvoiceListItem; activeAccountId: number; selected: boolean };

    const VCard = resolveComponent('v-card');
    const RouterLink = resolveComponent('router-link');
    const VDivider = resolveComponent('v-divider');

    const getCompanyNames = (contacts: { name: string }[]): string => (contacts.map(item => item.name).join(', ') || '');

    return h(VCard, {
      variant: 'outlined',
      ...this.$attrs,
      class: ['open-invoice-mobile-card', this.$attrs.class, props.selected ? 'open-invoice-mobile-card--selected' : ''],
      onClick: () => (this.$attrs['onClick:select'] as Function)(),
    }, { default: () => [
      h('div', { class: 'd-flex align-center pa-3' }, [
        h(RouterLink, {
          class: 'open-invoice-mobile-card__title app-link',
          to: getAccountRouteHandler({
            name: 'deal',
            params: { id: props.item.deal._key },
            activeAccountId: props.activeAccountId,
          }),
          onClick: (e: Event) => e.stopPropagation(),
        }, { default: () => props.item.deal.name }),
      ]),
      h(VDivider),
      h('div', { class: 'pa-3 d-flex flex-column gap-3' }, [
        h('div', { class: 'd-flex gap-3' }, [
          h('div', { class: 'open-invoice-mobile-card__info' }, [
            h('div', { class: 'open-invoice-mobile-card__info-label open-invoice-mobile-card__invoice-amount-label' }, 'Invoice Amount:'),
            h('div', { class: 'open-invoice-mobile-card__invoice-amount' }, formatCurrency(props.item.amount))
          ]),
          h('div', { class: 'open-invoice-mobile-card__info' }, [
            h('div', { class: 'open-invoice-mobile-card__info-label open-invoice-mobile-card__brokers-label' }, 'Brokers:'),
            h('div', { class: 'open-invoice-mobile-card__brokers' }, [
              h(Label, { value: props.item.deal.brokers.map(broker => `${broker.first_name} ${broker.last_name}`).join(', ').trim() || null }),
            ])
          ])
        ]),
        h('div', { class: 'd-flex gap-3' }, [
          h('div', { class: 'open-invoice-mobile-card__info' }, [
            h('div', { class: 'open-invoice-mobile-card__info-label open-invoice-mobile-card__due-date-label' }, 'Due Date:'),
            h('div', { class: 'open-invoice-mobile-card__due-date' }, formatDate(props.item.due_date))
          ]),
          h('div', { class: 'open-invoice-mobile-card__info' }, [
            h('div', { class: 'open-invoice-mobile-card__info-label open-invoice-mobile-card__buyer-tenant-label' }, 'Buyer/Tenant:'),
            h('div', { class: 'open-invoice-mobile-card__buyer-tenant' }, [
              h(Label, { value: getCompanyNames(props.item.buyer_tenant) }),
            ])
          ])
        ]),
        h('div', { class: 'd-flex gap-3' }, [
          h('div', { class: 'open-invoice-mobile-card__info' }, [
            h('div', { class: 'open-invoice-mobile-card__info-label open-invoice-mobile-card__outstanding-label' }, 'Outstanding:'),
            h('div', { class: 'open-invoice-mobile-card__remaining open-invoice-mobile-card__outstanding' }, formatCurrency(props.item.remaining))
          ]),
          h('div', { class: 'open-invoice-mobile-card__info' }, [
            h('div', { class: 'open-invoice-mobile-card__info-label open-invoice-mobile-card__seller-landlord-label' }, 'Seller/Landlord:'),
            h('div', { class: 'open-invoice-mobile-card__seller-landlord' }, [
              h(Label, { value: getCompanyNames(props.item.seller_landlord) })
            ])
          ])
        ])
      ])
    ] })
  }
});
</script>

<style lang="scss" scoped>
// The card is this component's root, so the rule sits on it directly (Vue 2's
// `::v-deep.x` compiled onto the root; `:deep()` only reaches descendants).
.open-invoice-mobile-card {
  position: relative;
  font-size: 13px;

  &.open-invoice-mobile-card--selected {
    border-color: rgb(var(--v-theme-primary));
  }

  :deep(.open-invoice-mobile-card__title) {
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }

  :deep(.open-invoice-mobile-card__info) {
    flex: 1 1 50%;
  }

  :deep(.open-invoice-mobile-card__info-label) {
    font-weight: 600;
  }
}
</style>
